import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { HERO_IMAGE, BUY_URL } from "@/data/products";

const LINES = ["BRAKE", "LATE.", "FINISH", "FIRST."];

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.76, 0, 0.24, 1], delay: 0.3 + i * 0.12 },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.6, 0.95]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative h-[100svh] w-full overflow-hidden bg-black"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={HERO_IMAGE}
          alt="Superbike cornering at speed on track"
          className="h-full w-full object-cover object-center"
        />
      </motion.div>

      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/40"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-transparent" />

      {/* Top telemetry label */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.8 }}
        className="absolute top-24 sm:top-28 left-5 sm:left-8 flex items-center gap-3"
      >
        <span className="h-2 w-2 bg-ebc-red animate-pulse" />
        <span className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-neutral-300">
          World Leading Brake Specialists
        </span>
      </motion.div>

      {/* Kinetic headline */}
      <div className="absolute bottom-0 left-0 right-0 px-5 sm:px-8 pb-8 sm:pb-12">
        <div className="max-w-[1600px] mx-auto">
          <h1 className="font-display uppercase text-white leading-[0.85] tracking-tight text-[13vw] sm:text-[10vw] lg:text-[7rem]">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden">
                <motion.span
                  custom={i}
                  variants={lineVariant}
                  initial="hidden"
                  animate="show"
                  className={`block ${i === 3 ? "text-ebc-red" : "text-white"}`}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="mt-4 sm:mt-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 sm:gap-6"
          >
            <p className="max-w-md text-sm sm:text-base text-neutral-300 leading-relaxed">
              USA & UK-made performance brake pads and rotors, trusted by 55,000+ dealers
              and race teams across the globe.
            </p>
            <a
              href={BUY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-buy-now"
              className="group inline-flex items-center gap-3 border border-white/25 hover:border-ebc-red bg-black/30 backdrop-blur px-6 py-4 transition-colors duration-300 self-start"
            >
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-white">
                Shop Motorcycle Range
              </span>
              <span className="h-8 w-8 grid place-items-center bg-ebc-red group-hover:bg-ebc-redhover transition-colors duration-300">
                <ArrowDown className="w-4 h-4 text-white -rotate-45" />
              </span>
            </a>
          </motion.div>
        </div>
      </div>

      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-6 right-6 hidden sm:flex items-center gap-2 text-neutral-400"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <ArrowDown className="w-4 h-4" />
      </motion.div>
    </section>
  );
};
